/* Team: one row per operator who worked through the CRM, with the AI as a tinted
   row of its own so people and the AI are read on the same columns. The design's
   nine columns show by default; "Columns" adds the CRM Operator Dashboard's other
   real ones (remembered per browser). Impact is a share of everything the AI and
   the Team sold. Response time is a median, so the Total row is the server's median
   over every response, never a sum of rows. The toolbar renders into the tab bar (``toolbarSlot``). */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { ChatStatsResponse, StatisticsResponse, StatisticsResponseTimes, StatisticsTeamRow } from '../../../api/client';
import { C } from '../../ChatAssistant/compact/tokens';
import { font as f } from '../../ChatAssistant/compact/style';
import { Icon } from '../../ChatAssistant/compact/kit';
import { aiActivity, aiMoney } from './derive';
import { downloadCsv, fmt, ledgerMoney, pct, ratio, usd, usd2, type MoneyMode } from './model';

type ColId =
  | 'accounts' | 'total_sales' | 'impact' | 'messages' | 'ppv_sent' | 'ppv_sold' | 'open_rate' | 'chats'
  | 'tips' | 'ppv_sales' | 'rev_per_chat' | 'manually_typed' | 'free_media' | 'purchase_time'
  | 'avg_sent' | 'avg_sold' | 'ppv_free' | 'ppv_messages' | 'chat_ppv' | 'words' | 'avg_words'
  | 'response_time' | 'quick_replies';

const DEFAULT_COLS: ColId[] = ['accounts', 'total_sales', 'impact', 'messages', 'ppv_sent', 'ppv_sold', 'open_rate', 'chats', 'response_time', 'quick_replies'];
// Default columns added after people saved their own set: shown to them once.
const ADDED_COLS: ColId[] = ['response_time', 'quick_replies'];
const ADDED_KEY = 'stx-team-cols-added-response';
const EXTRA_COLS: ColId[] = ['tips', 'ppv_sales', 'rev_per_chat', 'manually_typed', 'free_media', 'purchase_time', 'avg_sent', 'avg_sold', 'ppv_free', 'ppv_messages', 'chat_ppv', 'words', 'avg_words'];
const WIDTH: Partial<Record<ColId, string>> = { accounts: '70px', total_sales: '100px', impact: '150px', messages: '90px', response_time: '100px', quick_replies: '84px' };
const COLS_KEY = 'stx-team-cols';

function readCols(): ColId[] {
  try {
    const raw = JSON.parse(localStorage.getItem(COLS_KEY) || 'null');
    if (Array.isArray(raw)) {
      const saved = raw.filter((c): c is ColId => [...DEFAULT_COLS, ...EXTRA_COLS].includes(c));
      if (localStorage.getItem(ADDED_KEY)) return saved;
      localStorage.setItem(ADDED_KEY, '1');
      return DEFAULT_COLS.concat(EXTRA_COLS).filter((c) => saved.includes(c) || ADDED_COLS.includes(c));
    }
    localStorage.setItem(ADDED_KEY, '1');
  } catch { /* storage blocked: defaults */ }
  return DEFAULT_COLS;
}

/** A row's values, null = not applicable ("—"). Money already in the page's mode. */
interface Vals {
  accounts: number; total_sales: number; tips: number | null; ppv_sales: number | null;
  messages: number; ppv_sent: number; ppv_sold: number; chats: number;
  manually_typed: number | null; free_media: number | null; purchase_time: number | null;
  avg_sent: number | null; avg_sold: number | null; words: number | null; avg_words: number | null;
  responses: StatisticsResponseTimes | null;
}

const hasResponses = (r: StatisticsResponseTimes | undefined): StatisticsResponseTimes | null => (r?.count ? r : null);

function operatorVals(r: StatisticsTeamRow, mode: MoneyMode, responses: StatisticsResponseTimes | undefined): Vals {
  return {
    accounts: r.account_ids.length,
    total_sales: ledgerMoney(r.total_sales, mode),
    tips: ledgerMoney(r.tips, mode),
    ppv_sales: ledgerMoney(r.ppv_sales, mode),
    messages: r.total_messages,
    ppv_sent: r.ppv_sent,
    ppv_sold: r.ppv_sold,
    chats: r.chats_count,
    manually_typed: r.total_messages,
    free_media: r.free_media_sent,
    purchase_time: r.ppv_sold ? r.avg_ppv_purchase_seconds : null,
    avg_sent: r.ppv_sent ? ledgerMoney(r.avg_sent_ppv_price, mode) : null,
    avg_sold: r.ppv_sold ? ledgerMoney(r.avg_sold_ppv_price, mode) : null,
    words: r.words,
    avg_words: r.word_tracked_messages ? r.words / r.word_tracked_messages : null,
    responses: hasResponses(responses),
  };
}

const duration = (s: number) => (s >= 3600 ? `${Math.floor(s / 3600)}h ${Math.round((s % 3600) / 60)}m` : s >= 60 ? `${Math.round(s / 60)}m` : `${Math.round(s)}s`);

interface Props {
  stats: StatisticsResponse | null;
  chat: ChatStatsResponse | null;
  mode: MoneyMode;
  accountScoped: boolean;
  toolbarSlot: HTMLElement | null;
}

export function TeamTable({ stats, chat, mode, accountScoped, toolbarSlot }: Props) {
  const { t } = useTranslation('statsUnified');
  const [cols, setCols] = useState<ColId[]>(readCols);
  const [colMenu, setColMenu] = useState(false);
  const [opMenu, setOpMenu] = useState(false);
  const [operator, setOperator] = useState<string | null>(null);
  useEffect(() => { try { localStorage.setItem(COLS_KEY, JSON.stringify(cols)); } catch { /* ignore */ } }, [cols]);

  const team = stats?.team ?? [];
  const a = aiActivity(chat?.totals);
  const money = aiMoney(stats, mode);
  const aiVals: Vals = {
    accounts: accountScoped ? 1 : chat?.accounts.length ?? 0,
    total_sales: money.revenue, tips: money.tips, ppv_sales: money.ppv,
    messages: a.messages, ppv_sent: a.ppvSent, ppv_sold: money.ppvSold, chats: a.chats,
    manually_typed: null, free_media: null, purchase_time: null, avg_sent: null,
    avg_sold: money.ppvSold ? money.ppv / money.ppvSold : null, words: null, avg_words: null,
    responses: hasResponses(stats?.responses?.ai),
  };
  const ops = team.map((r) => ({ id: r.operator_id, name: r.operator_username, v: operatorVals(r, mode, stats?.responses?.operators[r.operator_id]) }));
  const everyone = aiVals.total_sales + ops.reduce((s, o) => s + o.v.total_sales, 0);
  const shown = operator ? ops.filter((o) => o.id === operator) : ops;
  const rows = [...(operator ? [] : [{ id: 'ai', name: 'AI', v: aiVals, isAi: true }]), ...shown.map((o) => ({ ...o, isAi: false }))];
  const sum = (k: keyof Vals) => rows.reduce((s, r) => s + Number(r.v[k] ?? 0), 0);
  const totals: Vals = {
    // Every account anyone in the table worked: the AI's and the operators'.
    accounts: operator
      ? new Set(team.filter((r) => r.operator_id === operator).flatMap((r) => r.account_ids)).size
      : accountScoped ? 1 : new Set([...(chat?.accounts ?? []).map((x) => x.account_id), ...team.flatMap((r) => r.account_ids)]).size,
    total_sales: sum('total_sales'), tips: sum('tips'), ppv_sales: sum('ppv_sales'),
    messages: sum('messages'), ppv_sent: sum('ppv_sent'), ppv_sold: sum('ppv_sold'),
    // A fan two operators wrote to is one chat: count the Team's chats per account.
    chats: operator ? sum('chats') : aiVals.chats + (stats?.team_accounts ?? []).reduce((s, x) => s + x.chats, 0),
    manually_typed: sum('manually_typed'), free_media: sum('free_media'), purchase_time: null,
    avg_sent: null, avg_sold: sum('ppv_sold') ? sum('ppv_sales') / sum('ppv_sold') : null, words: sum('words'), avg_words: null,
    responses: operator ? hasResponses(stats?.responses?.operators[operator]) : hasResponses(stats?.responses?.all),
  };

  const cell = (id: ColId, v: Vals): ReactNode => {
    switch (id) {
      case 'accounts': return fmt(v.accounts);
      case 'total_sales': return usd(v.total_sales);
      case 'impact': return pct(ratio(v.total_sales, everyone) * 100, 0);
      case 'messages': return fmt(v.messages);
      case 'ppv_sent': return fmt(v.ppv_sent);
      case 'ppv_sold': return fmt(v.ppv_sold);
      case 'open_rate': return v.ppv_sent ? pct(ratio(v.ppv_sold, v.ppv_sent) * 100) : null;
      case 'chats': return fmt(v.chats);
      case 'tips': return v.tips === null ? null : usd(v.tips);
      case 'ppv_sales': return v.ppv_sales === null ? null : usd(v.ppv_sales);
      case 'rev_per_chat': return v.chats ? usd2(v.total_sales / v.chats) : null;
      case 'manually_typed': return v.manually_typed === null ? null : fmt(v.manually_typed);
      case 'free_media': return v.free_media === null ? null : fmt(v.free_media);
      case 'purchase_time': return v.purchase_time === null ? null : duration(v.purchase_time);
      case 'avg_sent': return v.avg_sent === null ? null : usd2(v.avg_sent);
      case 'avg_sold': return v.avg_sold === null ? null : usd2(v.avg_sold);
      case 'ppv_free': return v.free_media ? (v.ppv_sent / v.free_media).toFixed(2) : null;
      case 'ppv_messages': return v.messages && v.free_media !== null ? (v.ppv_sent / v.messages).toFixed(2) : null;
      case 'chat_ppv': return v.ppv_sent && v.free_media !== null ? (v.chats / v.ppv_sent).toFixed(2) : null;
      case 'words': return v.words === null ? null : fmt(v.words);
      case 'avg_words': return v.avg_words === null ? null : v.avg_words.toFixed(1);
      case 'response_time': return v.responses?.median_seconds == null ? null : duration(v.responses.median_seconds);
      case 'quick_replies': return v.responses ? pct(ratio(v.responses.quick, v.responses.count) * 100, 0) : null;
    }
  };
  // The median's cell carries the average and how many responses it is over.
  const cellTitle = (id: ColId, v: Vals): string | undefined =>
    id === 'response_time' && v.responses?.avg_seconds != null
      ? t('team.responseTitle', { avg: duration(v.responses.avg_seconds), count: v.responses.count })
      : undefined;

  const grid = `minmax(150px,1.4fr) ${cols.map((c) => WIDTH[c] ?? '90px').join(' ')}`;
  const minWidth = 180 + cols.reduce((s, c) => s + parseInt(WIDTH[c] ?? '90', 10) + 12, 0);
  const allCols = useMemo(() => [...DEFAULT_COLS, ...EXTRA_COLS], []);

  const exportCsv = () => {
    const header = [t('team.member'), ...cols.map((c) => t(`team.cols.${c}`))];
    const body = rows.map((r) => [r.name, ...cols.map((c) => { const x = cell(c, r.v); return typeof x === 'string' ? x : ''; })]);
    downloadCsv(`statistics-team-${stats?.range.start ?? ''}_${stats?.range.end ?? ''}.csv`, [header, ...body]);
  };

  const btn: React.CSSProperties = { height: 28, padding: '0 10px', display: 'flex', alignItems: 'center', gap: 6, background: C.input, border: `1px solid ${C.borderHi}`, borderRadius: 6, color: 'var(--om-body)', font: f(500, 12), whiteSpace: 'nowrap', cursor: 'pointer' };
  const menu: React.CSSProperties = { position: 'absolute', top: 32, right: 0, zIndex: 21, width: 240, padding: 4, background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 8, boxShadow: '0 12px 32px rgba(0,0,0,.5)', display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 360, overflowY: 'auto' };
  const opName = ops.find((o) => o.id === operator)?.name;

  const toolbar = (
    <div style={{ display: 'flex', gap: 8 }}>
      <div style={{ position: 'relative' }}>
        <button type="button" style={btn} className="cpt-hover-border" onClick={() => setOpMenu((o) => !o)}>
          <Icon name="user" size={12} color={C.muted} />{opName ?? t('team.allOperators')}<Icon name="chevron-down" size={12} color={C.muted} />
        </button>
        {opMenu && (
          <>
            <div onClick={() => setOpMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
            <div style={menu} className="cpt-thin-scroll">
              {[{ id: null as string | null, name: t('team.allOperators') }, ...ops].map((o) => (
                <button key={o.id ?? 'all'} type="button" className="cpt-hover-row" onClick={() => { setOperator(o.id); setOpMenu(false); }} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 30, padding: '4px 8px', border: 0, borderRadius: 6, background: operator === o.id ? C.tint16 : 'transparent', color: C.text, font: f(500, 12.5), textAlign: 'left', cursor: 'pointer' }}>
                  <span style={{ flex: 1 }}>{o.name}</span>{operator === o.id && <Icon name="check" size={13} color={C.purpleLt} />}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
      <div style={{ position: 'relative' }}>
        <button type="button" style={btn} className="cpt-hover-border" onClick={() => setColMenu((o) => !o)}>
          <Icon name="columns" size={12} color={C.muted} />{t('team.columns')}
        </button>
        {colMenu && (
          <>
            <div onClick={() => setColMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
            <div style={menu} className="cpt-thin-scroll">
              {allCols.map((c) => {
                const on = cols.includes(c);
                return (
                  <label key={c} className="cpt-hover-row" style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 28, padding: '2px 8px', borderRadius: 6, font: f(500, 12.5), color: on ? C.text : C.sub, cursor: 'pointer' }}>
                    <input id={`stx-col-${c}`} type="checkbox" checked={on} onChange={() => setCols((cur) => (on ? cur.filter((x) => x !== c) : allCols.filter((x) => x === c || cur.includes(x))))} style={{ accentColor: C.purple }} />
                    {t(`team.cols.${c}`)}
                  </label>
                );
              })}
              <button type="button" onClick={() => setCols(DEFAULT_COLS)} style={{ margin: '4px 8px', alignSelf: 'flex-start', border: 0, padding: 0, background: 'transparent', color: C.purpleLt, font: f(500, 12), cursor: 'pointer' }}>{t('team.resetColumns')}</button>
            </div>
          </>
        )}
      </div>
      <button type="button" style={{ ...btn, background: 'transparent' }} className="cpt-hover-border" onClick={exportCsv}>
        <Icon name="download" size={12} color={C.muted} />CSV
      </button>
    </div>
  );

  return (
    <>
      {toolbarSlot && createPortal(toolbar, toolbarSlot)}
      <div className="cpt-thin-scroll" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, overflowX: 'auto' }}>
        <div style={{ minWidth }}>
          <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 12, alignItems: 'center', height: 34, padding: '0 14px', borderBottom: `1px solid ${C.divider}`, font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted }}>
            <span style={{ position: 'sticky', left: 0, background: C.card }}>{t('team.member')}</span>
            {cols.map((c) => <span key={c} title={t(`team.tips.${c}`, { defaultValue: '' }) || undefined} style={{ textAlign: c === 'impact' ? 'left' : 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t(`team.cols.${c}`)}</span>)}
          </div>
          {rows.map((r) => (
            <div key={r.id} style={{ display: 'grid', gridTemplateColumns: grid, gap: 12, alignItems: 'center', height: 40, padding: '0 14px', borderTop: `1px solid ${C.divider}`, background: r.isAi ? 'color-mix(in srgb, var(--om-accent) 10%, transparent)' : 'transparent', font: f(400, 12.5), color: 'var(--om-body)', fontVariantNumeric: 'tabular-nums' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: r.isAi ? 600 : 500, color: r.isAi ? C.purplePale : C.text, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.isAi && <Icon name="sparkles" size={13} />}{r.isAi ? t('sellers.ai') : r.name}
              </span>
              {cols.map((c) => {
                const v = cell(c, r.v);
                if (c === 'impact') {
                  const w = ratio(r.v.total_sales, everyone) * 100;
                  return (
                    <span key={c} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ flex: 1, height: 4, borderRadius: 99, background: C.border, overflow: 'hidden' }}><span style={{ display: 'block', height: '100%', width: `${w}%`, background: r.isAi ? C.purple : C.purplePale }} /></span>
                      <span style={{ width: 34, textAlign: 'right' }}>{v}</span>
                    </span>
                  );
                }
                return <span key={c} title={cellTitle(c, r.v)} style={{ textAlign: 'right', fontWeight: c === 'total_sales' ? 600 : 400, color: v === null ? C.faint : c === 'total_sales' ? C.text : undefined }}>{v ?? '—'}</span>;
              })}
            </div>
          ))}
          {!ops.length && (
            <div style={{ padding: '12px 14px', borderTop: `1px solid ${C.divider}`, font: f(400, 12.5), color: C.muted }}>{t('team.empty')}</div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 12, alignItems: 'center', height: 40, padding: '0 14px', borderTop: `1px solid ${C.borderHi}`, background: 'var(--om-card-2)', font: f(600, 12.5), color: C.text, fontVariantNumeric: 'tabular-nums' }}>
            <span>{t('ledger.total')}</span>
            {cols.map((c) => {
              const v = c === 'impact' ? (operator ? pct(ratio(totals.total_sales, everyone) * 100, 0) : '100%') : cell(c, totals);
              return <span key={c} title={cellTitle(c, totals)} style={{ textAlign: c === 'impact' ? 'right' : 'right', color: v === null ? C.faint : C.text }}>{v ?? '—'}</span>;
            })}
          </div>
        </div>
      </div>
    </>
  );
}
